import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { AiConversation, AiMessage } from '../../../domain/Ai';

const DISABLED_MESSAGE = 'El asistente de IA no está disponible en este momento.';

interface LocationState {
  firstQuestion?: string;
}

// Un mensaje tal como lo muestra el chat. `key` no cambia cuando el mensaje
// optimista se reemplaza por el que guardó el servidor, así la burbuja no se
// vuelve a montar (y no se corta la animación de tipeo).
export interface ChatMessage extends AiMessage {
  key: string;
  // streaming: el asistente lo está escribiendo; stopped: el usuario lo
  // detuvo; failed: la pregunta no tuvo respuesta (se puede reintentar).
  status?: 'streaming' | 'stopped' | 'failed';
}

const toChat = (m: AiMessage): ChatMessage => ({ ...m, key: m.id });

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

export function useAiConversation(conversationId: string) {
  const { ai: aiRepo } = useRepositories();
  const location = useLocation<LocationState | undefined>();

  const [conversation, setConversation] = useState<AiConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiDisabled, setAiDisabled] = useState(false);
  // La burbuja que se escribió en esta sesión: es la única que se anima
  // (las que vienen del historial aparecen completas).
  const [liveKey, setLiveKey] = useState<string | null>(null);

  const sentFirstQuestion = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setLoading(true);
    aiRepo
      .getConversation(conversationId)
      .then(({ conversation: found, messages: list }) => {
        setConversation(found);
        setMessages(list.map(toChat));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'No se pudo cargar la conversación.'))
      .finally(() => setLoading(false));
  }, [conversationId, aiRepo]);

  // Si se sale de la conversación con una respuesta a medio escribir, se corta.
  useEffect(() => () => abortRef.current?.abort(), []);

  const updateMessage = (key: string, patch: (m: ChatMessage) => Partial<ChatMessage>) =>
    setMessages((prev) => prev.map((m) => (m.key === key ? { ...m, ...patch(m) } : m)));

  const sendMessage = async (question: string) => {
    setSending(true);
    setError(null);
    const now = Date.now();
    const userKey = `user-${now}`;
    const assistantKey = `assistant-${now}`;
    const base = { conversationId, reasoning: null, reasoningMs: null, createdAt: new Date(now).toISOString() };
    // Optimista: la pregunta se ve al toque, y abajo la burbuja del asistente
    // que se va llenando a medida que llega la respuesta.
    setMessages((prev) => [
      ...prev,
      { ...base, id: userKey, key: userKey, role: 'user', content: question },
      { ...base, id: assistantKey, key: assistantKey, role: 'assistant', content: '', status: 'streaming' },
    ]);
    setLiveKey(assistantKey);

    // Los pedazos llegan muy seguido: se acumulan y se pasan al estado una vez
    // por frame en vez de re-renderizar con cada uno.
    let reasoning = '';
    let text = '';
    let reasoningMs: number | null = null;
    let frame: number | null = null;
    const flush = () => {
      frame = null;
      updateMessage(assistantKey, () => ({ reasoning: reasoning || null, content: text, reasoningMs }));
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(flush);
    };

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const result = await aiRepo.sendMessage(
        conversationId,
        question,
        {
          onReasoning: (delta) => {
            reasoning += delta;
            schedule();
          },
          onText: (delta) => {
            if (!text && reasoning) reasoningMs = Date.now() - now;
            text += delta;
            schedule();
          },
        },
        controller.signal
      );
      if (frame !== null) cancelAnimationFrame(frame);
      setConversation(result.conversation);
      setMessages((prev) =>
        prev.map((m) => {
          if (m.key === userKey) return { ...result.userMessage, key: userKey };
          if (m.key === assistantKey) return { ...result.assistantMessage, key: assistantKey };
          return m;
        })
      );
    } catch (err) {
      if (frame !== null) cancelAnimationFrame(frame);
      if (isAbort(err)) {
        // Detenido: queda lo que alcanzó a escribir (el servidor guarda lo mismo).
        if (text.trim()) updateMessage(assistantKey, () => ({ content: text, reasoning: reasoning || null, reasoningMs, status: 'stopped' }));
        else setMessages((prev) => prev.filter((m) => m.key !== assistantKey));
      } else {
        const msg = err instanceof Error ? err.message : 'No pudimos responder tu consulta.';
        if (msg === DISABLED_MESSAGE) setAiDisabled(true);
        else setError(msg);
        setMessages((prev) =>
          prev.filter((m) => m.key !== assistantKey).map((m) => (m.key === userKey ? { ...m, status: 'failed' } : m))
        );
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setSending(false);
    }
  };

  const stop = () => abortRef.current?.abort();

  // Vuelve a mandar la última pregunta que no tuvo respuesta.
  const retry = () => {
    const failed = [...messages].reverse().find((m) => m.status === 'failed');
    if (!failed || sending) return;
    setMessages((prev) => prev.filter((m) => m.key !== failed.key));
    sendMessage(failed.content);
  };

  useEffect(() => {
    const firstQuestion = location.state?.firstQuestion;
    if (firstQuestion && !loading && !sentFirstQuestion.current) {
      sentFirstQuestion.current = true;
      sendMessage(firstQuestion);
    }
  }, [loading]);

  return { conversation, messages, loading, sending, error, aiDisabled, liveKey, sendMessage, stop, retry };
}
